import {
  COMPANY_ADDRESS_FULL,
  COMPANY_LOCATION,
  COMPANY_MONOGRAM,
  COMPANY_NAME,
  COMPANY_SHORT,
  MAP_URL,
  PROFILE_FACTS,
  PUBLIC_INFO,
  SUPPORT_NOTES,
} from './constants';

function Header() {
  const navItems = [
    { label: 'Profile', href: '#profile' },
    { label: 'Public Info', href: '#public-info' },
    { label: 'Location', href: '#location' },
  ];

  return (
    <header className="sticky top-0 z-30 border-b border-line bg-white/95 backdrop-blur">
      <div className="mx-auto flex max-w-7xl items-center justify-between gap-4 px-5 py-4">
        <a href="#top" className="flex min-w-0 items-center gap-3" aria-label={`${COMPANY_SHORT} home`}>
          <span className="grid h-11 w-11 shrink-0 place-items-center rounded bg-haitiBlue text-xs font-black text-white ring-2 ring-haitiRed/20">
            {COMPANY_MONOGRAM}
          </span>
          <span className="truncate text-sm font-black tracking-wide text-charcoal sm:text-base">{COMPANY_SHORT}</span>
        </a>
        <nav className="hidden items-center gap-7 text-sm font-bold text-muted md:flex" aria-label="Primary">
          {navItems.map((item) => (
            <a key={item.href} href={item.href} className="transition hover:text-haitiBlue">
              {item.label}
            </a>
          ))}
        </nav>
        <a
          href="#location"
          className="rounded border border-haitiRed bg-haitiRed px-4 py-2 text-sm font-black text-white transition hover:border-haitiBlue hover:bg-haitiBlue"
        >
          View Address
        </a>
      </div>
    </header>
  );
}

function SectionHeading({ eyebrow, title, children }) {
  return (
    <div className="max-w-2xl">
      <p className="text-sm font-black uppercase tracking-[0.18em] text-haitiRed">{eyebrow}</p>
      <h2 className="mt-3 text-3xl font-black leading-tight text-charcoal sm:text-4xl">{title}</h2>
      {children ? <p className="mt-4 leading-7 text-muted">{children}</p> : null}
    </div>
  );
}

function Hero() {
  return (
    <section className="bg-warmWhite">
      <div className="h-3 bg-haitiBlue" />
      <div className="h-3 bg-haitiRed" />
      <div className="mx-auto max-w-7xl px-5 py-14 lg:py-20">
        <div className="grid gap-0 overflow-hidden rounded border border-line bg-white shadow-soft lg:grid-cols-[1.15fr_0.85fr]">
          <div className="p-7 sm:p-10 lg:p-12">
            <p className="text-sm font-black uppercase tracking-[0.22em] text-haitiRed">
              Springfield Haitian Community Profile
            </p>
            <h1 className="mt-5 max-w-5xl text-4xl font-black leading-tight tracking-normal text-charcoal sm:text-5xl lg:text-6xl">
              {COMPANY_NAME}
            </h1>
            <p className="mt-6 max-w-3xl text-lg leading-8 text-muted">
              A public reference page for a Springfield, Ohio organization focused on community help, support, and
              easy-to-find location information.
            </p>
            <div className="mt-8 grid gap-3 sm:grid-cols-2">
              <a
                href="#profile"
                className="rounded bg-haitiBlue px-5 py-3 text-center font-black text-white shadow-sm transition hover:bg-charcoal"
              >
                View Profile
              </a>
              <a
                href="#public-info"
                className="rounded border border-haitiRed px-5 py-3 text-center font-black text-haitiRed transition hover:bg-haitiRed hover:text-white"
              >
                Public Information
              </a>
            </div>
          </div>
          <div className="grid bg-cloud lg:grid-rows-[1fr_auto]">
            <div className="flex flex-col justify-between gap-10 bg-haitiBlue p-7 text-white sm:p-10">
              <div>
                <p className="text-xs font-black uppercase tracking-[0.22em] text-white/65">Community marker</p>
                <p className="mt-5 text-6xl font-black leading-none">{COMPANY_MONOGRAM}</p>
              </div>
              <div className="h-2 w-28 bg-gold" />
            </div>
            <div className="border-t-8 border-haitiRed bg-white p-7 sm:p-10">
              <p className="text-xs font-black uppercase tracking-[0.18em] text-haitiBlue">Public Address</p>
              <p className="mt-3 text-xl font-black leading-snug text-charcoal">{COMPANY_ADDRESS_FULL}</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function FactsStrip() {
  return (
    <section aria-label="Profile facts" className="bg-charcoal text-white">
      <div className="mx-auto grid max-w-7xl divide-y divide-white/15 px-5 md:grid-cols-3 md:divide-x md:divide-y-0">
        {PROFILE_FACTS.map((fact) => (
          <div key={fact.label} className="py-6 md:px-6 first:md:pl-0 last:md:pr-0">
            <p className="text-xs font-black uppercase tracking-[0.18em] text-gold">{fact.label}</p>
            <p className="mt-2 text-xl font-black text-white">{fact.value}</p>
          </div>
        ))}
      </div>
    </section>
  );
}

function ProfileSection() {
  return (
    <section id="profile" className="bg-paper">
      <div className="mx-auto grid max-w-7xl gap-10 px-5 py-16 lg:grid-cols-[0.58fr_1.42fr] lg:py-20">
        <SectionHeading eyebrow="Profile" title="A community identity page">
          {COMPANY_NAME} is listed at 1530 S Yellow Springs St in Springfield, Ohio. The site keeps the organization
          identity and address easy to find without adding unsupported operational details.
        </SectionHeading>
        <div className="border-y border-line">
          <div className="grid gap-5 border-b border-line py-7 md:grid-cols-[0.34fr_0.66fr]">
            <p className="text-xs font-black uppercase tracking-[0.18em] text-haitiRed">Organization</p>
            <p className="text-2xl font-black leading-snug text-charcoal">{COMPANY_NAME}</p>
          </div>
          <div className="grid gap-5 py-7 md:grid-cols-[0.34fr_0.66fr]">
            <p className="text-xs font-black uppercase tracking-[0.18em] text-haitiBlue">Profile Note</p>
            <p className="leading-8 text-muted">
              The page is intentionally conservative: it presents only supplied public profile data and leaves out
              private identifiers, phone numbers, emails, and service claims that were not provided.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

function PublicInfoSection() {
  return (
    <section id="public-info" className="bg-cloud">
      <div className="mx-auto max-w-7xl px-5 py-16 lg:py-20">
        <div className="flex flex-col gap-6 border-b border-line pb-10 lg:flex-row lg:items-end lg:justify-between">
          <SectionHeading eyebrow="Public Information" title="Source-backed details only">
            The layout separates verified organization details from descriptive copy, keeping the page useful without
            over-claiming.
          </SectionHeading>
          <p className="max-w-sm text-sm font-black uppercase tracking-[0.18em] text-haitiBlue">
            Directory-ready, public-safe, and easy to scan.
          </p>
        </div>
        <div className="mt-10 grid gap-8 lg:grid-cols-[1.1fr_0.9fr]">
          <div className="divide-y divide-line border-y border-line">
            {SUPPORT_NOTES.map((note) => (
              <article key={note.title} className="grid gap-4 py-7 md:grid-cols-[0.16fr_0.34fr_0.5fr] md:items-start">
                <p className="font-black text-haitiRed">{note.number}</p>
                <h3 className="text-xl font-black text-charcoal">{note.title}</h3>
                <p className="leading-7 text-muted">{note.body}</p>
              </article>
            ))}
          </div>
          <div className="rounded border border-line bg-white">
            {PUBLIC_INFO.map((item) => (
              <div key={item.label} className="border-b border-line p-6 last:border-b-0">
                <p className="text-xs font-black uppercase tracking-[0.18em] text-haitiBlue">{item.label}</p>
                <p className="mt-2 text-xl font-black text-charcoal">{item.value}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function LocationBand() {
  return (
    <section id="location" className="border-t-8 border-haitiRed bg-haitiBlue text-white">
      <div className="mx-auto grid max-w-7xl gap-8 px-5 py-16 lg:grid-cols-[0.8fr_1.2fr] lg:items-center">
        <div>
          <p className="text-sm font-black uppercase tracking-[0.18em] text-gold">Location</p>
          <h2 className="mt-3 text-3xl font-black sm:text-4xl">{COMPANY_LOCATION}</h2>
          <p className="mt-4 max-w-xl leading-7 text-white/75">
            Use the supplied address below for public profile, directory, and community reference.
          </p>
        </div>
        <div className="rounded border border-white/20 bg-white/10 p-6">
          <p className="text-xs font-black uppercase tracking-[0.18em] text-white/60">Organization Address</p>
          <p className="mt-3 text-2xl font-black">{COMPANY_ADDRESS_FULL}</p>
          <a
            href={MAP_URL}
            target="_blank"
            rel="noreferrer"
            className="mt-6 inline-flex rounded bg-white px-5 py-3 font-black text-haitiBlue transition hover:bg-gold hover:text-charcoal"
          >
            Open map
          </a>
        </div>
      </div>
    </section>
  );
}

function Footer() {
  return (
    <footer className="bg-charcoal text-white/70">
      <div className="mx-auto flex max-w-7xl flex-col gap-3 px-5 py-8 text-sm md:flex-row md:items-center md:justify-between">
        <p className="font-bold text-white">
          &copy; {new Date().getFullYear()} {COMPANY_NAME}
        </p>
        <p>{COMPANY_LOCATION}</p>
      </div>
    </footer>
  );
}

function App() {
  return (
    <div id="top" className="min-h-screen bg-warmWhite text-charcoal">
      <a
        href="#profile"
        className="sr-only focus:not-sr-only focus:absolute focus:left-3 focus:top-3 focus:z-50 focus:rounded focus:bg-haitiRed focus:px-3 focus:py-2 focus:text-white"
      >
        Skip to content
      </a>
      <Header />
      <main>
        <Hero />
        <FactsStrip />
        <ProfileSection />
        <PublicInfoSection />
        <LocationBand />
      </main>
      <Footer />
    </div>
  );
}

export default App;
